import { Mark, Node } from "@tiptap/core"
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state"
import Paragraph from "@tiptap/extension-paragraph"
import OrderedList from "@tiptap/extension-ordered-list"
import BulletList from "@tiptap/extension-bullet-list"
import ListItem from "@tiptap/extension-list-item"
import Youtube from "@tiptap/extension-youtube"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import { TableKit, TableCell, TableHeader, TableRow, Table } from "@tiptap/extension-table"
import { TableMap } from "@tiptap/pm/tables"
import { TextStyleKit } from "@tiptap/extension-text-style"

import { lowlight } from "@/lib/lowlight"
import { isAllowedEmbedURL } from "@/tiptap/core/safe-url"
import { normalizeFirstPartyFileSrc } from "@/tiptap/core/editor-clipboard"
import { parseInertHTML } from "@/tiptap/core/inert-html"

const LANGUAGE_ALIAS_MAP = (() => {
  const canonicalNames = new Set(lowlight.listLanguages())
  const aliases = {
    js: "javascript", jsx: "javascript", mjs: "javascript", cjs: "javascript",
    ts: "typescript", tsx: "typescript",
    py: "python", gyp: "python",
    rb: "ruby", gemspec: "ruby",
    sh: "bash", zsh: "bash",
    yml: "yaml",
    md: "markdown", mkd: "markdown", mkdown: "markdown",
    cs: "csharp",
    html: "xml", xhtml: "xml", rss: "xml", atom: "xml", svg: "xml",
    h: "c",
    hpp: "cpp", cc: "cpp", cxx: "cpp",
    pl: "perl", pm: "perl",
    fs: "fsharp",
    rs: "rust",
    hs: "haskell",
  }
  const map = new Map()
  for (const [alias, canonical] of Object.entries(aliases)) {
    if (canonicalNames.has(canonical)) map.set(alias, canonical)
  }
  return map
})()

const ROW_RESIZE_HANDLE_SIZE = 10
const MIN_ROW_HEIGHT = 30

function resolveLanguageAlias(alias) {
  if (!alias) return null
  const languages = lowlight.listLanguages()
  if (languages.includes(alias)) return alias
  return LANGUAGE_ALIAS_MAP.get(alias) || alias
}

function optionalAttribute(name) {
  return {
    default: null,
    parseHTML: (element) => element.getAttribute(name) || null,
    renderHTML: (attributes) => (attributes[name] ? { [name]: attributes[name] } : {}),
  }
}

function presenceAttribute(name, token = name, defaultValue = null) {
  return {
    default: defaultValue,
    parseHTML: (element) => (element.hasAttribute(name) ? token : null),
    renderHTML: (attributes) => (attributes[name] ? { [name]: attributes[name] } : {}),
  }
}

function createMarkAttributes(names) {
  return names.reduce((attributes, name) => {
    attributes[name] = optionalAttribute(name)
    return attributes
  }, {})
}

function createSimpleMark(name, tag, attributeNames) {
  return Mark.create({
    name,

    addAttributes() {
      return createMarkAttributes(attributeNames)
    },

    parseHTML() {
      return [{ tag }]
    },

    renderHTML({ HTMLAttributes }) {
      return [tag, HTMLAttributes, 0]
    },
  })
}

function createSimpleNode(name, tag, options = {}) {
  const {
    attrs = [],
    content,
    group,
    inline,
    atom,
    selectable,
  } = options

  return Node.create({
    name,
    ...(typeof content !== "undefined" ? { content } : {}),
    ...(typeof group !== "undefined" ? { group } : {}),
    ...(typeof inline !== "undefined" ? { inline } : {}),
    ...(typeof atom !== "undefined" ? { atom } : {}),
    ...(typeof selectable !== "undefined" ? { selectable } : {}),

    addAttributes() {
      return createMarkAttributes(attrs)
    },

    parseHTML() {
      return [{ tag }]
    },

    renderHTML({ HTMLAttributes }) {
      return [tag, HTMLAttributes, 0]
    },
  })
}

function stripManagedTextStyleProperties(styleValue) {
  if (!styleValue) return null

  const filteredDeclarations = styleValue
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .filter((declaration) => {
      const colon = declaration.indexOf(":")
      const property = (colon === -1 ? declaration : declaration.slice(0, colon)).trim().toLowerCase()

      // font-weight/font-style are owned by the Bold/Italic marks. GDocs writes
      // `font-weight: 400; font-style: normal` on plain runs, which would
      // otherwise override <strong>/<em> when the user toggles them later.
      return ![
        "color",
        "background-color",
        "font-family",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-color",
        "text-decoration-style",
        "text-decoration-thickness",
        "text-underline-offset",
      ].includes(property)
    })

  return filteredDeclarations.length > 0 ? filteredDeclarations.join("; ") : null
}

function extractStylePropertyValue(styleValue, propertyName) {
  if (!styleValue) return null

  const escapedProperty = propertyName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const match = styleValue.match(new RegExp(`(?:^|;)\\s*${escapedProperty}\\s*:\\s*([^;]+)`, "i"))

  return match?.[1]?.trim() || null
}

const Span = Mark.create({
  name: "span",

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => stripManagedTextStyleProperties(element.getAttribute("style")),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
      class: optionalAttribute("class"),
      id: optionalAttribute("id"),
      title: optionalAttribute("title"),
      "data-mce-style": optionalAttribute("data-mce-style"),
      "data-mce-object": optionalAttribute("data-mce-object"),
      contenteditable: optionalAttribute("contenteditable"),
    }
  },

  parseHTML() {
    return [
      {
        tag: "span",
        getAttrs: (element) => {
          if (element.getAttribute("data-type") === "emoji") return false
          return null
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0]
  },
})

const Cite = createSimpleMark("cite", "cite", ["style", "class", "id", "title"])
const Dfn = createSimpleMark("dfn", "dfn", ["style", "class", "id", "title"])
const Ins = createSimpleMark("ins", "ins", ["style", "class", "id", "datetime", "cite"])
const Kbd = createSimpleMark("kbd", "kbd", ["style", "class", "id", "title"])
const Label = Mark.create({
  name: "label",

  addAttributes() {
    return createMarkAttributes(["style", "class", "id", "for"])
  },

  parseHTML() {
    return [
      {
        tag: "label",
        getAttrs: (element) => {
          if (
            element instanceof HTMLElement &&
            element.closest('ul[data-type="taskList"] li[data-type="taskItem"]') &&
            element.querySelector('input[type="checkbox"]')
          ) {
            return false
          }

          return null
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["label", HTMLAttributes, 0]
  },
})

const IFRAME_ATTR_KEYS = [
  "src",
  "width",
  "height",
  "frameborder",
  "allowfullscreen",
  "allow",
  "style",
  "class",
  "title",
  "scrolling",
]

function applyIframeAttributes(iframe, attrs = {}) {
  IFRAME_ATTR_KEYS.forEach((key) => {
    const value = attrs[key]
    if (value == null || value === false) {
      iframe.removeAttribute(key)
      return
    }

    if (key === "class") {
      iframe.className = value
      return
    }

    // Last line of defense: this writes straight to a live iframe, so a
    // `javascript:` src here navigates and executes with this origin.
    if (key === "src" && !isAllowedEmbedURL(String(value))) {
      iframe.removeAttribute(key)
      return
    }

    iframe.setAttribute(key, value === true ? key : String(value))
  })
}

const Iframe = Node.create({
  name: "iframe",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      // Drop unsafe schemes on the way in so they never enter the document and
      // cannot round-trip back into storage on the next save.
      src: {
        default: null,
        parseHTML: (element) => {
          const src = element.getAttribute("src")

          return isAllowedEmbedURL(src) ? src : null
        },
        renderHTML: (attributes) => (attributes.src ? { src: attributes.src } : {}),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width") || element.style.width || null,
        renderHTML: (attributes) => (attributes.width ? { width: attributes.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height") || element.style.height || null,
        renderHTML: (attributes) => (attributes.height ? { height: attributes.height } : {}),
      },
      frameborder: {
        default: "0",
        parseHTML: (element) => element.getAttribute("frameborder") || "0",
        renderHTML: (attributes) => ({ frameborder: attributes.frameborder }),
      },
      allowfullscreen: {
        default: "true",
        parseHTML: (element) => element.getAttribute("allowfullscreen") || "true",
        renderHTML: (attributes) => ({ allowfullscreen: attributes.allowfullscreen }),
      },
      allow: optionalAttribute("allow"),
      style: optionalAttribute("style"),
      class: optionalAttribute("class"),
      title: optionalAttribute("title"),
      scrolling: optionalAttribute("scrolling"),
    }
  },

  parseHTML() {
    return [{ tag: "iframe" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["iframe", HTMLAttributes]
  },

  // Wrap wide embeds (e.g. min-width: 640px) so they scroll inside the article
  // instead of expanding the page chrome (top nav / mobile toolbar).
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("div")
      dom.className = "tiptap-media-scroll"
      dom.setAttribute("data-media-scroll", "iframe")

      const iframe = document.createElement("iframe")
      applyIframeAttributes(iframe, node.attrs)
      dom.appendChild(iframe)

      return {
        dom,
        update(updatedNode) {
          if (updatedNode.type.name !== "iframe") return false
          applyIframeAttributes(iframe, updatedNode.attrs)
          return true
        },
        ignoreMutation: () => true,
      }
    }
  },
})

const Audio = Node.create({
  name: "audio",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      src: optionalAttribute("src"),
      controls: presenceAttribute("controls", "controls", "controls"),
      autoplay: presenceAttribute("autoplay"),
      loop: presenceAttribute("loop"),
      muted: presenceAttribute("muted"),
      preload: optionalAttribute("preload"),
      style: optionalAttribute("style"),
      class: optionalAttribute("class"),
    }
  },

  parseHTML() {
    return [
      {
        tag: "audio",
        getAttrs: (node) => {
          const firstSource = node.querySelector("source")

          return {
            src: normalizeFirstPartyFileSrc(
              node.getAttribute("src") || firstSource?.getAttribute("src") || null,
            ),
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["audio", HTMLAttributes]
  },
})

const Video = Node.create({
  name: "video",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      src: optionalAttribute("src"),
      poster: optionalAttribute("poster"),
      controls: presenceAttribute("controls", "controls", "controls"),
      autoplay: presenceAttribute("autoplay"),
      loop: presenceAttribute("loop"),
      muted: presenceAttribute("muted"),
      preload: optionalAttribute("preload"),
      width: optionalAttribute("width"),
      height: optionalAttribute("height"),
      style: optionalAttribute("style"),
      class: optionalAttribute("class"),
    }
  },

  parseHTML() {
    return [
      {
        tag: "video",
        getAttrs: (node) => {
          const firstSource = node.querySelector("source")

          return {
            src: normalizeFirstPartyFileSrc(
              node.getAttribute("src") || firstSource?.getAttribute("src") || null,
            ),
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["video", HTMLAttributes]
  },
})

const Div = Node.create({
  name: "div",
  content: "block*",
  group: "block",

  addAttributes() {
    return createMarkAttributes(["class", "style", "id"])
  },

  parseHTML() {
    return [
      {
        tag: "div",
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return null

          // Editor chrome wrappers that other nodes own. If Div claims them,
          // source-editor save/reopen nests an extra <div> on every round-trip.
          if (element.matches("li[data-type='taskItem'] > div")) return false
          if (element.classList.contains("code-block-wrapper")) return false
          if (element.getAttribute("data-type") === "horizontalRule") return false

          return null
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", HTMLAttributes, 0]
  },
})

const Footer = createSimpleNode("footer", "footer", {
  content: "inline*",
  group: "block",
  attrs: ["class", "style", "id"],
})

const Figure = Node.create({
  name: "htmlFigure",
  content: "block+",
  group: "block",

  addAttributes() {
    return createMarkAttributes(["class", "style", "id"])
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (node) => {
          if (node.querySelector?.("img")) {
            return false
          }

          return null
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["figure", HTMLAttributes, 0]
  },
})

const Dd = createSimpleNode("dd", "dd", {
  content: "inline*",
  group: "block",
  attrs: ["class", "style", "id"],
})

const Input = Node.create({
  name: "input",
  inline: true,
  group: "inline",
  atom: false,
  selectable: false,

  addAttributes() {
    return {
      type: {
        default: "text",
        parseHTML: (element) => element.getAttribute("type") || "text",
        renderHTML: (attributes) => ({ type: attributes.type }),
      },
      checked: presenceAttribute("checked"),
      value: optionalAttribute("value"),
      "aria-label": optionalAttribute("aria-label"),
      class: optionalAttribute("class"),
      style: optionalAttribute("style"),
      disabled: presenceAttribute("disabled"),
      readonly: presenceAttribute("readonly"),
      placeholder: optionalAttribute("placeholder"),
      name: optionalAttribute("name"),
      min: optionalAttribute("min"),
      max: optionalAttribute("max"),
    }
  },

  parseHTML() {
    return [{
      tag: "input",
      getAttrs: (element) => {
        if (element.closest('ul[data-type="taskList"] li[data-type="taskItem"] > label')) {
          return false
        }

        return null
      },
    }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["input", HTMLAttributes]
  },
})

const Button = Node.create({
  name: "button",
  inline: true,
  group: "inline",
  atom: false,
  selectable: false,
  content: "text*",

  addAttributes() {
    return {
      type: {
        default: "button",
        parseHTML: (element) => element.getAttribute("type") || "button",
        renderHTML: (attributes) => ({ type: attributes.type }),
      },
      name: optionalAttribute("name"),
      value: optionalAttribute("value"),
      class: optionalAttribute("class"),
      style: optionalAttribute("style"),
      id: optionalAttribute("id"),
      disabled: presenceAttribute("disabled"),
      "aria-label": optionalAttribute("aria-label"),
    }
  },

  parseHTML() {
    return [{ tag: "button" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["button", HTMLAttributes, 0]
  },
})

const Option = Node.create({
  name: "option",
  content: "text*",

  addAttributes() {
    return {
      value: optionalAttribute("value"),
      selected: presenceAttribute("selected"),
      disabled: presenceAttribute("disabled"),
      label: optionalAttribute("label"),
    }
  },

  parseHTML() {
    return [{ tag: "option" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["option", HTMLAttributes, 0]
  },
})

const Select = Node.create({
  name: "select",
  content: "option+",
  inline: true,
  group: "inline",

  addAttributes() {
    return {
      name: optionalAttribute("name"),
      class: optionalAttribute("class"),
      style: optionalAttribute("style"),
      id: optionalAttribute("id"),
      disabled: presenceAttribute("disabled"),
      multiple: presenceAttribute("multiple"),
      size: optionalAttribute("size"),
      "aria-label": optionalAttribute("aria-label"),
    }
  },

  parseHTML() {
    return [
      {
        tag: "select",
        getAttrs: (element) => {
          if (
            element instanceof HTMLElement &&
            element.classList.contains("code-block-language-selector")
          ) {
            return false
          }

          return null
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["select", HTMLAttributes, 0]
  },
})

const EnhancedParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        // Indent owns data-indent. Saved paragraphs also pick up
        // margin-left / padding-left / text-indent from the editor CSS.
        // Keeping those on style freezes the visible indent after reload.
        parseHTML: (element) =>
          removeStyleProperties(element.getAttribute("style"), [
            "--tt-indent-level",
            "margin-left",
            "padding-left",
            "text-indent",
          ]),
        renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
      },
      class: optionalAttribute("class"),
      dir: optionalAttribute("dir"),
    }
  },
})

const EnhancedOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: optionalAttribute("style"),
      class: optionalAttribute("class"),
    }
  },
})

const EnhancedListItem = ListItem.extend({
  // Allow a whole-item code block without an empty leading paragraph.
  // TipTap's default `paragraph block*` forces an empty <p> first; hiding that
  // with display:none drops the list marker in Safari.
  content: "(paragraph | codeBlock) block*",

  addAttributes() {
    return {
      ...this.parent?.(),
      class: optionalAttribute("class"),
      style: optionalAttribute("style"),
    }
  },
})

const EnhancedBulletList = BulletList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: optionalAttribute("style"),
      class: optionalAttribute("class"),
      role: optionalAttribute("role"),
    }
  },
})

function extractWidthValue(element) {
  if (!element) return null

  return (
    element.getAttribute("width") ||
    element.style.width ||
    extractStylePropertyValue(element.getAttribute("style"), "width") ||
    extractStylePropertyValue(element.getAttribute("data-mce-style"), "width") ||
    null
  )
}

function isPercentageWidth(widthValue) {
  return typeof widthValue === "string" && widthValue.trim().endsWith("%")
}

function getEditableCellWidth(element) {
  const width = extractWidthValue(element)

  if (width && !isPercentageWidth(width)) {
    return width
  }

  return getColumnWidthForCell(element)
}

function extractResizableColwidthValue(element) {
  const width = extractWidthValue(element)
  return extractResizableCssWidthValue(width)
}

function extractResizableCssWidthValue(width) {
  if (!width) return null

  if (typeof width === "number") return width

  const trimmedWidth = width.trim()
  const pixelMatch = trimmedWidth.match(/^(\d+(?:\.\d+)?)(px)?$/i)

  if (!pixelMatch) return null

  return Math.round(Number.parseFloat(pixelMatch[1]))
}

function extractMinWidthValue(element) {
  if (!element) return null

  return (
    element.style.minWidth ||
    extractStylePropertyValue(element.getAttribute("style"), "min-width") ||
    null
  )
}

// Used when switching a legacy/saved table into TipTap resize mode. The saved
// table can keep visual styles like borders, but width/min-width must be
// removed so the live table width follows the resized columns.
function removeStyleProperties(style, properties) {
  if (!style) return null

  const propertiesToRemove = new Set(properties.map((property) => property.toLowerCase()))
  const nextStyle = style
    .split(";")
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const [property] = declaration.split(":")
      return property && !propertiesToRemove.has(property.trim().toLowerCase())
    })
    .join("; ")

  return nextStyle ? `${nextStyle};` : null
}

function getCellColumnIndex(element) {
  let index = 0
  let sibling = element.previousElementSibling

  while (sibling) {
    const colspan = Number.parseInt(sibling.getAttribute("colspan") || "1", 10)
    index += Number.isNaN(colspan) ? 1 : colspan
    sibling = sibling.previousElementSibling
  }

  return index
}

// Legacy TinyMCE tables often store column sizing on <col> elements. When a
// cell lacks TipTap's pixel-based colwidth attr, use the matching <col> width
// so the editor starts from the saved layout instead of defaulting every column.
function getColumnWidthForCell(element) {
  const table = element.closest("table")
  if (!table) return null

  const cols = table.querySelectorAll("colgroup > col")
  const col = cols[getCellColumnIndex(element)]

  return extractWidthValue(col)
}

function getResizableColumnWidthForCell(element) {
  const table = element.closest("table")
  if (!table) return null

  const cols = table.querySelectorAll("colgroup > col")
  const col = cols[getCellColumnIndex(element)]

  return extractResizableColwidthValue(col)
}

function getExpandedColumnCount(row) {
  let count = 0

  for (let i = 0; i < row.childCount; i += 1) {
    const colspan = Number(row.child(i).attrs.colspan) || 1
    count += colspan
  }

  return count
}

function rowHasStoredColwidths(row) {
  for (let i = 0; i < row.childCount; i += 1) {
    const colwidth = row.child(i).attrs.colwidth
    if (Array.isArray(colwidth) && colwidth.length > 0) {
      return true
    }
  }

  return false
}

function getReferenceTableRow(node) {
  let bestRow = null
  let bestExpandedColumns = -1
  let bestChildCount = -1
  let bestHasStoredColwidths = false

  for (let i = 0; i < node.childCount; i += 1) {
    const row = node.child(i)
    const expandedColumns = getExpandedColumnCount(row)
    const childCount = row.childCount
    const hasStoredColwidths = rowHasStoredColwidths(row)

    if (
      bestRow === null ||
      hasStoredColwidths > bestHasStoredColwidths ||
      (hasStoredColwidths === bestHasStoredColwidths && expandedColumns > bestExpandedColumns) ||
      (hasStoredColwidths === bestHasStoredColwidths &&
        expandedColumns === bestExpandedColumns &&
        childCount > bestChildCount)
    ) {
      bestRow = row
      bestExpandedColumns = expandedColumns
      bestChildCount = childCount
      bestHasStoredColwidths = hasStoredColwidths
    }
  }

  return bestRow
}

function tableHasStoredColwidths(node) {
  for (let i = 0; i < node.childCount; i += 1) {
    if (rowHasStoredColwidths(node.child(i))) {
      return true
    }
  }

  return false
}

// True when the style string contains a width/min-width declaration, whatever
// its formatting. String comparison against removeStyleProperties output is
// not enough: it re-canonicalizes spacing, so a non-canonical imported style
// (e.g. "border-collapse:collapse") would look changed despite having no width.
function styleHasWidthDeclaration(style) {
  if (!style) return false

  return style.split(";").some((declaration) => {
    const [property] = declaration.split(":")
    const name = property?.trim().toLowerCase()
    return name === "width" || name === "min-width"
  })
}

// True when any cell carries explicit width information (a pixel colwidth or a
// saved width attr). Tables without any — e.g. markdown-pasted tables — have
// nothing for the resize-normalization step to convert.
function tableHasAnyCellWidths(node) {
  for (let rowIndex = 0; rowIndex < node.childCount; rowIndex += 1) {
    const row = node.child(rowIndex)

    for (let cellIndex = 0; cellIndex < row.childCount; cellIndex += 1) {
      const { colwidth, width } = row.child(cellIndex).attrs
      if (Array.isArray(colwidth) && colwidth.length > 0) return true
      if (width) return true
    }
  }

  return false
}

// Check if a table has complete stored colwidths.
// This is used to determine if we need to normalize the table widths before editing.
// If the table has complete stored colwidths, we don't need to normalize the table widths.
function tableHasCompleteStoredColwidths(node) {
  for (let rowIndex = 0; rowIndex < node.childCount; rowIndex += 1) {
    const row = node.child(rowIndex)

    for (let cellIndex = 0; cellIndex < row.childCount; cellIndex += 1) {
      const cell = row.child(cellIndex)
      const colspan = Number(cell.attrs.colspan) || 1
      const colwidth = cell.attrs.colwidth

      if (!Array.isArray(colwidth) || colwidth.length < colspan) return false
      if (colwidth.some((width) => typeof width !== "number" || width <= 0)) return false
    }
  }

  return true
}

function getColStyleDeclaration(minWidth, width) {
  if (width) {
    return ["width", `${Math.max(width, minWidth)}px`]
  }

  return ["min-width", `${minWidth}px`]
}

function applyColStyle(colElement, cellMinWidth, pixelWidth, preservedWidth = null) {
  if (pixelWidth) {
    const [propertyKey, propertyValue] = getColStyleDeclaration(cellMinWidth, pixelWidth)
    colElement.style.removeProperty("min-width")
    colElement.style.setProperty(propertyKey, propertyValue)
    return
  }

  if (preservedWidth) {
    colElement.style.removeProperty("min-width")
    colElement.style.width = preservedWidth
    return
  }

  colElement.style.removeProperty("width")
  colElement.style.minWidth = `${cellMinWidth}px`
}

function getRenderedColWidth(colElement) {
  if (!colElement || typeof colElement.getBoundingClientRect !== "function") return null

  const measuredWidth = colElement.getBoundingClientRect().width
  if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) return null

  return Math.round(measuredWidth)
}

// TipTap/prosemirror-tables resizes using pixel colwidth attrs. Saved TinyMCE
// or rendered HTML can use percentages instead, so before resize we convert the
// current rendered column width into pixels and write it back to colwidth.
function getColumnResizeStartWidth(colElement, tableElement) {
  const renderedWidth = getRenderedColWidth(colElement)
  if (renderedWidth) return renderedWidth

  const pixelWidth = extractResizableColwidthValue(colElement)
  if (pixelWidth) return pixelWidth

  const tableWidth = getRenderedColWidth(tableElement)
  const width = extractWidthValue(colElement)
  if (tableWidth && isPercentageWidth(width)) {
    return Math.round((Number.parseFloat(width) / 100) * tableWidth)
  }

  return extractResizableCssWidthValue(extractMinWidthValue(colElement))
}

// Build a neutral description of what each <col> should be. This keeps the
// width decision separate from DOM mutation, which matters because resize
// updates can come from saved percent widths, TipTap colwidth attrs, or a
// temporary drag override.
function buildTableColumnPlan(node, colgroup, overrideCol, overrideValue) {
  const row = getReferenceTableRow(node)
  const hasStoredColwidths = tableHasStoredColwidths(node)
  const shouldUsePreservedWidths = !hasStoredColwidths && typeof overrideValue === "undefined"
  const shouldLockRenderedWidths = hasStoredColwidths || typeof overrideValue === "number"
  const existingCols = Array.from(colgroup.children)
  const columns = []
  let totalWidth = 0
  let numericWidthCount = 0

  if (row !== null) {
    for (let i = 0, col = 0; i < row.childCount; i += 1) {
      const { colspan, colwidth, width: cellWidth } = row.child(i).attrs

      for (let j = 0; j < colspan; j += 1, col += 1) {
        const storedWidth = overrideCol === col ? overrideValue : colwidth && colwidth[j]
        const fallbackRenderedWidth =
          typeof storedWidth === "number" || !shouldLockRenderedWidths
            ? null
            : getRenderedColWidth(existingCols[col])
        const pixelWidth = storedWidth ?? fallbackRenderedWidth
        const preservedWidth = shouldUsePreservedWidths
          ? (j === 0 && isPercentageWidth(cellWidth) ? cellWidth : null)
          : null

        if (typeof pixelWidth === "number") {
          totalWidth += pixelWidth
          numericWidthCount += 1
        }

        columns.push({
          pixelWidth,
          preservedWidth,
          cssWidth: pixelWidth ? `${pixelWidth}px` : preservedWidth || "",
        })
      }
    }
  }

  return {
    columns,
    hasCompleteNumericWidths: columns.length > 0 && numericWidthCount === columns.length,
    hasStoredColwidths,
    totalWidth,
  }
}

// Apply the planned column widths to the live editor DOM. This is editor-only:
// saved HTML is handled separately by editor serialization.
function syncColgroup(columnPlan, colgroup, cellMinWidth) {
  columnPlan.columns.forEach(({ pixelWidth, preservedWidth, cssWidth }, index) => {
    let colElement = colgroup.children[index]

    if (!colElement) {
      colElement = document.createElement("col")
      colgroup.appendChild(colElement)
    }

    applyColStyle(colElement, cellMinWidth, pixelWidth, preservedWidth)
  })

  while (colgroup.children.length > columnPlan.columns.length) {
    colgroup.removeChild(colgroup.lastChild)
  }
}

// Keep the live <table> width in sync with pixel columns during editing. When
// all columns have numeric widths, the table width becomes their sum; otherwise
// saved user width/min-width styles are respected.
function syncTableWidth(table, node, columnPlan, overrideValue) {
  const styleText = typeof node.attrs.style === "string" ? node.attrs.style : ""
  const hasUserWidth = /\bwidth\s*:/i.test(styleText)
  const hasUserMinWidth = /\bmin-width\s*:/i.test(styleText)

  if (
    (columnPlan.hasStoredColwidths || typeof overrideValue === "number") &&
    columnPlan.hasCompleteNumericWidths
  ) {
    table.style.width = `${columnPlan.totalWidth}px`
  } else if (!hasUserWidth) {
    table.style.width = ""
  }

  if (!hasUserMinWidth) {
    table.style.minWidth = ""
  }
}

// Custom version of TipTap's table column updater. It preserves imported
// percentage widths for display, but cooperates with TipTap's pixel-based
// resizing when the user drags a column border.
function updateColumnsWithoutTableWidth(
  node,
  colgroup,
  table,
  cellMinWidth,
  overrideCol,
  overrideValue
) {
  const columnPlan = buildTableColumnPlan(node, colgroup, overrideCol, overrideValue)
  syncColgroup(columnPlan, colgroup, cellMinWidth)
  syncTableWidth(table, node, columnPlan, overrideValue)
}

// TipTap's resize plugin expects column widths in pixel colwidth attrs. Older
// TinyMCE/saved tables often come back as table/col percentages so they render
// responsively in views. This node view bridges those worlds: display saved
// percentages normally, but normalize to pixel colwidths before resize starts.
class NoAutoTableWidthView {
  constructor(node, cellMinWidth, view = null) {
    this.node = node
    this.cellMinWidth = cellMinWidth
    this.view = view
    this._maxWidth = null

    this.dom = document.createElement("div")
    this.dom.className = "tableWrapper"

    this.scrollWrapper = this.dom.appendChild(document.createElement("div"))
    this.scrollWrapper.className = "tableWrapper-scroll"

    this.table = this.scrollWrapper.appendChild(document.createElement("table"))

    if (node.attrs.style) {
      this.table.style.cssText = node.attrs.style
    }

    if (node.attrs.class) {
      this.table.className = node.attrs.class
    } else {
      this.table.removeAttribute("class")
    }

    this.colgroup = this.table.appendChild(document.createElement("colgroup"))
    updateColumnsWithoutTableWidth(node, this.colgroup, this.table, cellMinWidth)
    this.contentDOM = this.table.appendChild(document.createElement("tbody"))

    this.controls = this.dom.appendChild(document.createElement("div"))
    this.controls.className = "table-controls"

    this.selectionOverlay = this.dom.appendChild(document.createElement("div"))
    this.selectionOverlay.className = "table-selection-overlay-container"

    this.rowResizeHandle = this.dom.appendChild(document.createElement("div"))
    this.rowResizeHandle.className = "row-resize-handle"
    this.rowResizeMask = this.dom.appendChild(document.createElement("div"))
    this.rowResizeMask.className = "row-resize-mask"

    this.handleResizePrepare = (event) => this.normalizeColumnWidthsForResize(event)
    this.handleColumnAddPrepare = (event) => this.normalizeColumnWidthsForColumnAdd(event)
    this.handleRowResizeMouseMove = (event) => this.rowResizeMouseMove(event)
    this.handleRowResizeMouseDown = (event) => this.rowResizeMouseDown(event)
    this.handleRowResizeMouseUp = (event) => this.rowResizeMouseUp(event)
    this.table.addEventListener("mousemove", this.handleResizePrepare, true)
    this.table.addEventListener("mousemove", this.handleRowResizeMouseMove, true)
    this.table.addEventListener("mousedown", this.handleRowResizeMouseDown, true)
    this.controls.addEventListener("mouseover", this.handleColumnAddPrepare, true)
    window.addEventListener("mousemove", this.handleRowResizeMouseMove)
    window.addEventListener("mouseup", this.handleRowResizeMouseUp)
  }

  update(node) {
    if (node.type !== this.node.type) {
      return false
    }

    this.node = node
    this.table.style.cssText = node.attrs.style || ""
    if (node.attrs.class) {
      this.table.className = node.attrs.class
    } else {
      this.table.removeAttribute("class")
    }
    updateColumnsWithoutTableWidth(node, this.colgroup, this.table, this.cellMinWidth)
    this.syncScrollWrapperMaxWidth(node)

    return true
  }

  // Only cap the scrollport after columns have been resized (stored colwidths).
  // update() runs on any table content change (typing, header toggle, etc.);
  // locking max-width there freezes the current viewport and breaks width:100%.
  syncScrollWrapperMaxWidth(node) {
    if (!tableHasStoredColwidths(node)) {
      this.scrollWrapper.style.maxWidth = ""
      return
    }

    if (this._maxWidth == null && this.dom.isConnected) {
      this._maxWidth = this.dom.getBoundingClientRect().width
    }
    if (this._maxWidth != null) {
      this.scrollWrapper.style.maxWidth = `${this._maxWidth}px`
    }
  }

  // Prepare percent-based tables before the mouse goes down on a resize border.
  // If this runs only on mousedown, ProseMirror may treat the first click as a
  // normal cell selection; preparing on hover lets the built-in resize plugin
  // start dragging on the first press.
  normalizeColumnWidthsForResize(event) {
    if (!this.isColumnResizeStart(event)) return
    this.normalizeColumnWidthsForEditing()
  }

  // Prepare percent-based tables before the mouse goes down on a column add/remove button.
  normalizeColumnWidthsForColumnAdd(event) {
    if (!event.target?.closest?.(".tiptap-table-column-end-add-remove")) return
    this.normalizeColumnWidthsForEditing()
  }

  // Prepare percent-based tables before the user starts editing a table.
  // Turns saved percentages into pixel colwidths.
  normalizeColumnWidthsForEditing() {
    if (!this.view) return

    const tablePos = this.findTablePosition()
    if (tablePos === null) return

    const tableNode = this.view.state.doc.nodeAt(tablePos)
    if (!tableNode) return

    const nextTableStyle = removeStyleProperties(tableNode.attrs.style, ["width", "min-width"])
    if (nextTableStyle === tableNode.attrs.style && tableHasCompleteStoredColwidths(tableNode)) {
        return;
    }

    // Nothing to normalize: no width declaration in the table style and no
    // saved cell widths/percentages to convert. This is a markdown-pasted (or
    // freshly inserted) fluid table — baking the current rendered widths here
    // would freeze the editor viewport (e.g. sidebar open) into the document
    // just from hovering near a cell edge. TipTap's resize plugin measures the
    // DOM on drag start, so an actual drag still works and stores colwidths.
    if (
      !styleHasWidthDeclaration(tableNode.attrs.style) &&
      !tableHasAnyCellWidths(tableNode)
    ) {
      return
    }

    const columnWidths = Array.from(this.colgroup.children).map((col) =>
      getColumnResizeStartWidth(col, this.table)
    )
    if (columnWidths.some((width) => !width)) return

    const map = TableMap.get(tableNode)
    const tableStart = tablePos + 1
    const seenCells = new Set()
    let tr = this.view.state.tr
    let changed = false

    if (nextTableStyle !== tableNode.attrs.style) {
      tr = tr.setNodeMarkup(tablePos, null, {
        ...tableNode.attrs,
        style: nextTableStyle,
      })
      changed = true
    }

    for (let row = 0; row < map.height; row += 1) {
      for (let col = 0; col < map.width; col += 1) {
        const cellOffset = map.map[row * map.width + col]
        if (seenCells.has(cellOffset)) continue
        seenCells.add(cellOffset)

        const cell = tableNode.nodeAt(cellOffset)
        if (!cell) continue

        const startColumn = map.colCount(cellOffset)
        const nextColwidth = Array.from({ length: cell.attrs.colspan }, (_, index) =>
          columnWidths[startColumn + index]
        )

        if (JSON.stringify(cell.attrs.colwidth) === JSON.stringify(nextColwidth)) continue

        tr = tr.setNodeMarkup(tableStart + cellOffset, null, {
          ...cell.attrs,
          colwidth: nextColwidth,
        })
        changed = true
      }
    }

    if (changed) this.view.dispatch(tr)
  }

  isColumnResizeStart(event) {
    if (event.target?.closest?.(".column-resize-handle")) return true

    const cell = event.target?.closest?.("td, th")
    if (!cell || !this.table.contains(cell)) return false

    const rect = cell.getBoundingClientRect()
    return rect.right - event.clientX <= 8 || event.clientX - rect.left <= 8
  }

  rowResizeMouseMove(event) {
    if (this.rowResizeState) {
      event.preventDefault()
      const nextHeight = Math.max(
        MIN_ROW_HEIGHT,
        this.rowResizeState.startHeight + event.clientY - this.rowResizeState.startY
      )

      this.setRowPreviewHeight(this.rowResizeState.rowElement, nextHeight)
      this.rowResizeState.nextHeight = nextHeight
      this.positionRowResizeHandle(
        this.rowResizeState.rowElement,
        this.rowResizeState.startTop + nextHeight
      )
      return
    }

    const target = this.getRowResizeTarget(event)
    this.table.style.cursor = target ? "row-resize" : ""
    if (target) {
      this.showRowResizeHandle(target.rowElement)
    } else {
      this.hideRowResizeHandle()
    }
  }

  rowResizeMouseDown(event) {
    const target = this.getRowResizeTarget(event)
    if (!target) return

    event.preventDefault()
    event.stopPropagation()

    const rowRect = target.rowElement.getBoundingClientRect()
    this.rowResizeState = {
      rowElement: target.rowElement,
      rowPos: target.rowPos,
      startY: event.clientY,
      startTop: rowRect.top,
      startHeight: rowRect.height,
      nextHeight: rowRect.height,
      naturalHeight: this.measureNaturalRowHeight(target.rowElement),
    }

    this.table.style.cursor = "row-resize"
    this.view?.dom.classList.add("row-resize-cursor")
    this.setRowResizeDragging(target.rowElement)
    this.showRowResizeHandle(target.rowElement)
    this.showRowResizeMask(target.rowElement)
  }

  rowResizeMouseUp() {
    if (!this.rowResizeState) return

    const { rowElement, rowPos, nextHeight, naturalHeight } = this.rowResizeState
    const shouldClearHeight = nextHeight <= naturalHeight + 1
    this.rowResizeState = null
    this.table.style.cursor = ""
    this.view?.dom.classList.remove("row-resize-cursor")
    this.clearRowResizeDragging()
    this.hideRowResizeHandle()
    this.hideRowResizeMask()

    if (!this.view) return

    const rowNode = this.view.state.doc.nodeAt(rowPos)

    if (!rowNode || rowNode.type.name !== "tableRow") return

    if (shouldClearHeight) {
      this.clearRowPreviewHeight(rowElement)
    }

    const height = shouldClearHeight ? null : `${Math.round(Math.max(MIN_ROW_HEIGHT, nextHeight))}px`
    this.view.dispatch(this.view.state.tr.setNodeMarkup(rowPos, null, {
      ...rowNode.attrs,
      height,
    }, rowNode.marks))
  }

  getRowResizeTarget(event) {
    if (!this.view?.editable) return null

    const cell = event.target?.closest?.("td, th")
    if (!cell || !this.table.contains(cell)) return null

    const rowElement = cell.closest("tr")
    if (!rowElement || !this.contentDOM.contains(rowElement)) return null

    const rows = Array.from(this.contentDOM.children)
    const rowIndex = rows.indexOf(rowElement)
    if (rowIndex === -1) return null

    const rect = rowElement.getBoundingClientRect()
    let targetRowIndex = null

    if (rect.bottom - event.clientY <= ROW_RESIZE_HANDLE_SIZE) {
      targetRowIndex = rowIndex
    } else if (rowIndex > 0 && event.clientY - rect.top <= ROW_RESIZE_HANDLE_SIZE) {
      targetRowIndex = rowIndex - 1
    }

    if (targetRowIndex === null) return null

    const rowPos = this.getRowPosition(targetRowIndex)
    if (rowPos === null) return null

    return {
      rowElement: rows[targetRowIndex],
      rowIndex: targetRowIndex,
      rowPos,
    }
  }

  getRowPosition(rowIndex) {
    const tablePos = this.findTablePosition()
    if (tablePos === null) return null

    const tableNode = this.view?.state.doc.nodeAt(tablePos)
    if (!tableNode || rowIndex < 0 || rowIndex >= tableNode.childCount) return null

    let rowPos = null
    tableNode.forEach((_rowNode, offset, index) => {
      if (index === rowIndex) {
        rowPos = tablePos + 1 + offset
        return false
      }

      return true
    })

    return rowPos
  }

  showRowResizeHandle(rowElement) {
    this.positionRowResizeHandle(rowElement)
    this.rowResizeHandle.classList.add("is-visible")
  }

  hideRowResizeHandle() {
    this.rowResizeHandle.classList.remove("is-visible")
  }

  showRowResizeMask(rowElement) {
    this.positionRowResizeMask(rowElement)
    this.rowResizeMask.classList.add("is-visible")
  }

  hideRowResizeMask() {
    this.rowResizeMask.classList.remove("is-visible")
  }

  setRowPreviewHeight(rowElement, height) {
    rowElement.style.height = `${height}px`
    rowElement.querySelectorAll(":scope > td, :scope > th").forEach((cell) => {
      cell.style.height = `${height}px`
    })
  }

  clearRowPreviewHeight(rowElement) {
    rowElement.style.height = ""
    rowElement.querySelectorAll(":scope > td, :scope > th").forEach((cell) => {
      cell.style.height = ""
    })
  }

  measureNaturalRowHeight(rowElement) {
    const rowHeight = rowElement.style.height
    const cells = Array.from(rowElement.querySelectorAll(":scope > td, :scope > th"))
    const cellHeights = cells.map((cell) => cell.style.height)

    rowElement.style.height = ""
    cells.forEach((cell) => {
      cell.style.height = ""
    })

    const naturalHeight = rowElement.getBoundingClientRect().height

    rowElement.style.height = rowHeight
    cells.forEach((cell, index) => {
      cell.style.height = cellHeights[index]
    })

    return naturalHeight
  }

  setRowResizeDragging(rowElement) {
    this.clearRowResizeDragging()
    rowElement.classList.add("row-resize-dragging")
    rowElement.nextElementSibling?.classList.add("row-resize-next-row")
  }

  clearRowResizeDragging() {
    this.table.querySelectorAll(".row-resize-dragging, .row-resize-next-row").forEach((row) => {
      row.classList.remove("row-resize-dragging", "row-resize-next-row")
    })
  }

  positionRowResizeHandle(rowElement, boundaryY = null) {
    const rowRect = rowElement.getBoundingClientRect()
    const tableRect = this.table.getBoundingClientRect()
    const scrollRect = this.scrollWrapper.getBoundingClientRect()
    const wrapperRect = this.dom.getBoundingClientRect()
    const left = Math.max(tableRect.left, scrollRect.left)
    const right = Math.min(tableRect.right, scrollRect.right)
    const width = Math.max(0, right - left)
    const y = Math.min(Math.max(boundaryY ?? rowRect.bottom, tableRect.top), tableRect.bottom)

    this.rowResizeHandle.style.left = `${left - wrapperRect.left}px`
    this.rowResizeHandle.style.top = `${y - wrapperRect.top - 1}px`
    this.rowResizeHandle.style.width = `${width}px`
  }

  positionRowResizeMask(rowElement) {
    const rowRect = rowElement.getBoundingClientRect()
    const tableRect = this.table.getBoundingClientRect()
    const scrollRect = this.scrollWrapper.getBoundingClientRect()
    const wrapperRect = this.dom.getBoundingClientRect()
    const left = Math.max(tableRect.left, scrollRect.left)
    const right = Math.min(tableRect.right, scrollRect.right)
    const width = Math.max(0, right - left)

    this.rowResizeMask.style.left = `${left - wrapperRect.left}px`
    this.rowResizeMask.style.top = `${rowRect.bottom - wrapperRect.top - 1}px`
    this.rowResizeMask.style.width = `${width}px`
  }

  findTablePosition() {
    let foundPos = null

    this.view?.state.doc.descendants((node, pos) => {
      if (node === this.node) {
        foundPos = pos
        return false
      }

      return true
    })

    return foundPos
  }

  destroy() {
    this.table.removeEventListener("mousemove", this.handleResizePrepare, true)
    this.table.removeEventListener("mousemove", this.handleRowResizeMouseMove, true)
    this.table.removeEventListener("mousedown", this.handleRowResizeMouseDown, true)
    this.controls.removeEventListener("mouseover", this.handleColumnAddPrepare, true)
    window.removeEventListener("mousemove", this.handleRowResizeMouseMove)
    window.removeEventListener("mouseup", this.handleRowResizeMouseUp)
    this.view?.dom.classList.remove("row-resize-cursor")
    this.clearRowResizeDragging()
    this.hideRowResizeHandle()
    this.hideRowResizeMask()
  }

  ignoreMutation(mutation) {
    const target = mutation.target
    const isInsideWrapper = this.dom.contains(target)
    const isInsideContent = this.contentDOM.contains(target)

    if (
      this.rowResizeState &&
      mutation.type === "attributes" &&
      target === this.rowResizeState.rowElement
    ) {
      return true
    }

    if (isInsideWrapper && !isInsideContent) {
      if (
        mutation.type === "attributes" ||
        mutation.type === "childList" ||
        mutation.type === "characterData"
      ) {
        return true
      }
    }

    return false
  }
}

const colwidthAttribute = {
  default: null,
  parseHTML: (element) => {
    const colwidth = element.getAttribute("colwidth")
    const value = colwidth
      ? colwidth
        .split(",")
        .map((width) => {
          const parsed = Number.parseInt(width.trim(), 10)
          return Number.isNaN(parsed) ? null : parsed
        })
        .filter((width) => width !== null)
      : null

    if (!value || value.length === 0) {
      const colWidth = getResizableColumnWidthForCell(element)

      if (colWidth) {
        return [colWidth]
      }
    }

    const directWidth = extractResizableColwidthValue(element)
    if ((!value || value.length === 0) && directWidth) {
      return [directWidth]
    }

    return value
  },
  renderHTML: (attributes) => {
    if (!attributes.colwidth) return {}
    return { colwidth: attributes.colwidth.join(",") }
  },
}

// Tiptap's TableCell/TableHeader ship a built-in `align` attribute that also
// parses/renders the cell's inline text-align. Hudu manages cell alignment via
// NodeAlignment's `nodeTextAlign`; keeping both makes the stale `align` value
// win after a document is saved and reloaded (its text-align renders last in
// the merged style attribute), so alignment changes stop taking effect.
const neutralizedAlignAttribute = {
  default: null,
  parseHTML: () => null,
  renderHTML: () => ({}),
}

// Move markdown-parsed `align` onto nodeTextAlign (see parseMarkdown on
// TableWithStyles below). Only walks nodes produced by the markdown parser.
function remapMarkdownTableCellAlign(node) {
  if (!node || typeof node !== "object") return node

  const isTableCell = node.type === "tableCell" || node.type === "tableHeader"
  const align = node.attrs?.align
  let nextNode = node

  if (isTableCell && align && !node.attrs.nodeTextAlign) {
    nextNode = {
      ...node,
      attrs: {
        ...node.attrs,
        nodeTextAlign: align,
        align: null,
      },
    }
  }

  if (Array.isArray(nextNode.content)) {
    return {
      ...nextNode,
      content: nextNode.content.map(remapMarkdownTableCellAlign),
    }
  }

  return nextNode
}

const TableCellWithBorders = TableCell.extend({
  allowGapCursor: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      align: neutralizedAlignAttribute,
      borderWidth: {
        default: null,
        parseHTML: (element) => element.style.borderWidth || element.style.borderTopWidth || null,
        renderHTML: (attributes) =>
          attributes.borderWidth ? { style: `border-width: ${attributes.borderWidth}` } : {},
      },
      borderColor: {
        default: null,
        parseHTML: (element) => element.style.borderColor || element.style.borderTopColor || null,
        renderHTML: (attributes) =>
          attributes.borderColor ? { style: `border-color: ${attributes.borderColor}` } : {},
      },
      width: {
        default: null,
        parseHTML: (element) => getEditableCellWidth(element),
        renderHTML: (attributes) =>
          attributes.width && !isPercentageWidth(attributes.width)
            ? { style: `width: ${attributes.width}` }
            : {},
      },
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) =>
          attributes.backgroundColor
            ? { style: `background-color: ${attributes.backgroundColor}` }
            : {},
      },
      colwidth: colwidthAttribute,
    }
  },

  renderHTML({ HTMLAttributes }) {
    const {
      borderWidth,
      borderColor,
      width,
      backgroundColor,
      colwidth,
      style = "",
      ...rest
    } = HTMLAttributes
    const shouldRenderWidth = width && !colwidth
    const styles =
      [
        style,
        borderWidth ? `border-width: ${borderWidth}` : "",
        borderColor ? `border-color: ${borderColor}` : "",
        shouldRenderWidth ? `width: ${width}` : "",
        backgroundColor ? `background-color: ${backgroundColor}` : "",
      ]
        .filter(Boolean)
        .join("; ") + ";"

    return ["td", { ...rest, style: styles || undefined }, 0]
  },
})

function createColGroupWithPercentages(node) {
  const cols = []
  const firstRow = getReferenceTableRow(node)

  if (!firstRow) return { colgroup: ["colgroup"] }

  let col = 0

  for (let i = 0; i < firstRow.childCount; i += 1) {
    const { colspan, colwidth, width } = firstRow.child(i).attrs

    for (let j = 0; j < colspan; j += 1, col += 1) {
      const pxWidth = (colwidth && colwidth[j]) || (j === 0 ? extractResizableColwidthValue({ 
        getAttribute: () => null,
        style: { width: typeof width === "string" ? width : "" },
      }) : null)

      if (pxWidth) {
        cols.push(["col", { style: `width: ${pxWidth}px` }])
      } else if (j === 0 && isPercentageWidth(width)) {
        cols.push(["col", { style: `width: ${width}` }])
      } else {
        cols.push(["col", {}])
      }
    }
  }

  return { colgroup: ["colgroup", {}, ...cols] }
}

const TableWithStyles = Table.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      View: NoAutoTableWidthView,
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      class: optionalAttribute("class"),
      style: optionalAttribute("style"),
      id: optionalAttribute("id"),
      border: optionalAttribute("border"),
      width: {
        default: null,
        parseHTML: (element) => extractWidthValue(element),
        renderHTML: () => ({}),
      },
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    const { width, ...rest } = HTMLAttributes
    const { colgroup } = createColGroupWithPercentages(node)
    return ["table", rest, colgroup, ["tbody", 0]]
  },

  // Markdown paste/import only. TipTap's table parser stores GFM `:-:` in the
  // built-in `align` attr, which Hudu neutralizes (nodeTextAlign owns cell
  // alignment), so copy it across or center columns are lost after paste.
  parseMarkdown: (token, helpers) => {
    const parsed = Table.config.parseMarkdown?.(token, helpers)
    return remapMarkdownTableCellAlign(parsed)
  },
})

const EnhancedTableHeader = TableHeader.extend({
  allowGapCursor: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      align: neutralizedAlignAttribute,
      width: {
        default: null,
        parseHTML: (element) => getEditableCellWidth(element),
        renderHTML: (attributes) =>
          attributes.width && !isPercentageWidth(attributes.width)
            ? { style: `width: ${attributes.width}` }
            : {},
      },
      colwidth: colwidthAttribute,
    }
  },

  parseHTML() {
    return [
      { tag: "th" },
      { tag: "td", getAttrs: (node) => node.parentNode?.parentNode?.nodeName === "THEAD" && null },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { width, colwidth, style = "", ...rest } = HTMLAttributes
    const styles = [style, width && !colwidth ? `width: ${width}` : ""].filter(Boolean).join("; ")

    return ["th", { ...rest, style: styles || undefined }, 0]
  },
})

const EnhancedTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      height: {
        default: null,
        parseHTML: (element) => element.style.height || null,
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    const { height, ...rest } = HTMLAttributes
    const styleAttr = height ? `height: ${height}` : undefined
    return ["tr", { ...rest, style: styleAttr }, 0]
  },
})

const EnhancedTableKit = TableKit.configure({
  tableCell: false,
  table: false,
  tableHeader: false,
  tableRow: false,
})

function detailsSummaryDepth($pos) {
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === "detailsSummary") return depth
  }

  return 0
}

function selectionIsInsideOneDetailsSummary(selection) {
  const summaryDepth = detailsSummaryDepth(selection.$from)
  if (!summaryDepth) return 0
  if (selection.$to.depth < summaryDepth) return 0
  if (selection.$to.node(summaryDepth) !== selection.$from.node(summaryDepth)) return 0

  return summaryDepth
}

// Firefox focuses <summary> and leaves the ProseMirror selection wherever it
// last was (often the heading that was just copied). Map the DOM target back
// to the accordion title so paste/caret land there.
function resolveDetailsSummaryFromDom(view, event) {
  const doc = view.dom?.ownerDocument
  const candidates = [event?.target, doc?.activeElement]

  for (const target of candidates) {
    if (!(target instanceof Element)) continue
    const summary = target.closest("summary")
    if (!summary || !view.dom.contains(summary)) continue

    try {
      const pos = view.posAtDOM(summary, 0)
      const $pos = view.state.doc.resolve(pos)
      const depth = detailsSummaryDepth($pos)
      if (depth) return { $pos, depth }
    } catch {
      // posAtDOM throws when the node is not in the current view
    }
  }

  return null
}

function selectDetailsSummaryFromDom(view, event) {
  const mapped = resolveDetailsSummaryFromDom(view, event)
  if (!mapped) return false

  let pos = mapped.$pos.pos
  if (event?.clientX != null && event?.clientY != null) {
    const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
    if (coords && detailsSummaryDepth(view.state.doc.resolve(coords.pos)) === mapped.depth) {
      pos = coords.pos
    }
  }

  view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos))))
  view.focus()
  return true
}

const CustomDetails = Node.create({
  name: "details",
  group: "block",
  content: "detailsSummary detailsContent",

  addAttributes() {
    return {
      open: {
        default: null,
        parseHTML: (element) => (element.hasAttribute("open") ? "open" : null),
        renderHTML: (attributes) => (attributes.open ? { open: "open" } : {}),
      },
      class: {
        default: "mce-accordion",
        parseHTML: (element) => element.getAttribute("class") || "mce-accordion",
        renderHTML: (attributes) => ({ class: attributes.class }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "details" }]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement("details")
      const contentDOM = dom

      const applyAttributes = (currentNode) => {
        dom.className = currentNode.attrs.class || "mce-accordion"

        if (currentNode.attrs.open) {
          dom.setAttribute("open", "open")
        } else {
          dom.removeAttribute("open")
        }
      }

      const toggleOpen = () => {
        if (typeof getPos !== "function") return

        const pos = getPos()
        const { state, view } = editor
        const nextOpen = node.attrs.open ? null : "open"
        const tr = state.tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          open: nextOpen,
        })

        // Opening/closing details reflows the page. On mobile, the browser
        // often scrolls the focused caret (which may be far away) back into
        // view — preserve scroll position across the toggle.
        const { scrollX, scrollY } = window
        view.dispatch(tr)
        requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY)
        })
      }

      const summaryContainsTarget = (target) => {
        const summary = target instanceof Element ? target.closest("summary") : null
        return summary && summary.closest("details") === dom
      }

      // Firefox focuses <summary>, so PM never sees the drag and the
      // floating toolbar stays hidden. Copy the native range on mouseup only
      // when posAtDOM maps to the same text — element-level endpoints would
      // otherwise snap to the whole title. Chrome already has a PM range.
      // Do not steal focus on focusin: that runs selectionToDOM with a
      // collapsed caret and wipes the native highlight in Chrome.
      dom.addEventListener("mouseup", (event) => {
        if (!editor.isEditable) return
        if (!summaryContainsTarget(event.target)) return

        const view = editor.view
        const native = view.dom.ownerDocument.getSelection()
        const saved = native?.rangeCount ? native.getRangeAt(0).cloneRange() : null

        if (!view.hasFocus()) {
          view.dom.focus({ preventScroll: true })
          if (saved && native) {
            native.removeAllRanges()
            native.addRange(saved)
          }
        }
        if (!saved) return
        // Firefox click: native range is collapsed and PM is still wherever it
        // last was (often a copied heading). Put the caret in the title so
        // Space/typing land there. Chrome already has the title caret.
        if (saved.collapsed) {
          selectDetailsSummaryFromDom(view, event)
          return
        }

        try {
          const from = view.posAtDOM(saved.startContainer, saved.startOffset)
          const to = view.posAtDOM(saved.endContainer, saved.endOffset)
          if (from === to) return

          const start = Math.min(from, to)
          const end = Math.max(from, to)
          if (view.state.doc.textBetween(start, end) !== saved.toString()) return

          view.dispatch(view.state.tr.setSelection(
            TextSelection.create(view.state.doc, start, end),
          ))
        } catch {
          // posAtDOM throws when the range is outside the current view
        }
      })

      // Fallback: if paste targets the summary while the PM caret is still
      // outside the title (e.g. after copying a heading), move it in first.
      dom.addEventListener("paste", (event) => {
        if (!editor.isEditable) return
        if (!summaryContainsTarget(event.target)) return
        if (selectionIsInsideOneDetailsSummary(editor.state.selection)) return
        selectDetailsSummaryFromDom(editor.view, event)
      }, true)

      dom.addEventListener("click", (event) => {
        if (!summaryContainsTarget(event.target)) return

        event.preventDefault()
        event.stopPropagation()
        toggleOpen()
      })

      dom.addEventListener("keydown", (event) => {
        if (!summaryContainsTarget(event.target)) return
        if (event.key !== "Enter" && event.key !== " ") return

        // Fallback: Firefox still targets the summary for keys when it
        // remains focused (Tab into the title). Space is text input, not a
        // toggle: block native details activation and type the character.
        if (editor.isEditable && event.key === " ") {
          event.preventDefault()
          event.stopPropagation()
          editor.view.dispatch(editor.state.tr.insertText(" "))
          // Firefox leaves DOM focus on the summary element, so ProseMirror
          // skips restoring the DOM caret after the redraw and it collapses
          // to the start. Refocusing the view syncs it back from state.
          editor.view.focus()
          return
        }

        event.preventDefault()
        event.stopPropagation()
        toggleOpen()
      })

      applyAttributes(node)

      return {
        dom,
        contentDOM,
        update(updatedNode) {
          if (updatedNode.type !== node.type) {
            return false
          }

          node = updatedNode
          applyAttributes(updatedNode)
          return true
        },
      }
    }
  },

  renderHTML({ HTMLAttributes }) {
    return ["details", HTMLAttributes, 0]
  },
})

const ACCORDION_TITLE_PASTE_KEY = new PluginKey("accordionTitlePaste")
const ACCORDION_HEADING_LEVELS = [1, 2, 3, 4]

function firstHeadingInFragment(fragment) {
  let heading = null

  fragment.forEach((node) => {
    if (heading) return
    if (node.type.name === "heading") {
      heading = node
      return
    }
    heading = firstHeadingInFragment(node.content)
  })

  return heading
}

function headingFromClipboardHTML(html, schema) {
  if (!html || !schema.nodes.heading) return null

  const headingEl = parseInertHTML(html).querySelector("h1, h2, h3, h4")
  if (!headingEl?.textContent) return null

  const level = Number(headingEl.tagName.slice(1))
  return schema.nodes.heading.create(
    { level: ACCORDION_HEADING_LEVELS.includes(level) ? level : 4 },
    schema.text(headingEl.textContent),
  )
}

const CustomDetailsSummary = Node.create({
  name: "detailsSummary",
  // TinyMCE allowed a heading in the accordion title so it appeared in the
  // article TOC. `inline*` made the title itself the textblock, so turning
  // it into a heading replaced the summary and ProseMirror lifted the
  // heading into the accordion body. Keep the title as a block wrapper.
  // Paragraph first so plain title text stays a paragraph. `heading | paragraph`
  // made ProseMirror wrap untitled text as a heading.
  content: "paragraph | heading",
  defining: true,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: ACCORDION_TITLE_PASTE_KEY,
        props: {
          // Empty title → become the pasted heading. Filled title keeps its
          // paragraph/heading and inserts the pasted text at the caret.
          handlePaste: (view, event, slice) => {
            const { state } = view
            let { $from, from, to } = state.selection
            let summaryDepth = selectionIsInsideOneDetailsSummary(state.selection)

            // Firefox: caret is often still on the copied heading. If paste
            // targeted the summary, treat that as the title.
            if (!summaryDepth) {
              const mapped = resolveDetailsSummaryFromDom(view, event)
              if (!mapped) return false
              summaryDepth = mapped.depth
              // posAtDOM(<summary>) is the block gap before the title
              // paragraph/heading. Filled-title paste inserts inline text,
              // so move into that textblock first.
              const titleStart = Math.min(
                mapped.$pos.start(summaryDepth) + 1,
                mapped.$pos.end(summaryDepth),
              )
              $from = state.doc.resolve(titleStart)
              from = titleStart
              to = titleStart
            }

            const heading =
              firstHeadingInFragment(slice.content) ||
              headingFromClipboardHTML(event?.clipboardData?.getData?.("text/html"), state.schema)
            const headingType = state.schema.nodes.heading
            if (!heading || !headingType) return false

            const titleIsEmpty = !$from.node(summaryDepth).textContent.trim()

            const keepCaretInTitle = (tr, pos) => {
              const $pos = tr.doc.resolve(Math.min(pos, tr.doc.content.size))
              if (detailsSummaryDepth($pos) === summaryDepth) {
                return tr.setSelection(TextSelection.near($pos))
              }

              return tr.setSelection(TextSelection.near(tr.doc.resolve($from.start(summaryDepth))))
            }

            if (titleIsEmpty) {
              const level = ACCORDION_HEADING_LEVELS.includes(heading.attrs.level)
                ? heading.attrs.level
                : 4
              const headingNode = headingType.create({ level }, heading.content)
              const titleFrom = $from.start(summaryDepth)
              const tr = keepCaretInTitle(
                state.tr.replaceWith(titleFrom, $from.end(summaryDepth), headingNode),
                titleFrom + headingNode.content.size + 1,
              )
              view.dispatch(tr.scrollIntoView())
              view.focus()
              return true
            }

            const tr = keepCaretInTitle(
              state.tr.replaceWith(from, to, heading.content),
              from + heading.content.size,
            )
            view.dispatch(tr.scrollIntoView())
            view.focus()
            return true
          },
        },
      }),
    ]
  },

  addAttributes() {
    return {
      class: {
        default: "mce-accordion-summary",
        parseHTML: (element) => element.getAttribute("class") || "mce-accordion-summary",
        renderHTML: (attributes) => ({ class: attributes.class }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "summary" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["summary", HTMLAttributes, 0]
  },
})

const CustomDetailsContent = Node.create({
  name: "detailsContent",
  content: "block+",

  addAttributes() {
    return {
      class: {
        default: "mce-accordion-body",
        parseHTML: (element) => element.getAttribute("class") || "mce-accordion-body",
        renderHTML: (attributes) => ({ class: attributes.class }),
      },
    }
  },

  parseHTML() {
    return [
      { tag: 'div[class*="mce-accordion-body"]' },
      { tag: 'div[data-type="detailsContent"]' },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", { ...HTMLAttributes, "data-mce-bogus": "1" }, 0]
  },
})

const EnhancedTextStyleKit = TextStyleKit.configure({
  HTMLAttributes: {
    style: null,
    class: null,
  },
})

const EnhancedYoutube = Youtube.configure({
  inline: false,
  width: 640,
  height: 480,
  controls: true,
  nocookie: false,
  allowFullscreen: true,
  autoplay: false,
  modestBranding: true,
  HTMLAttributes: {
    style: null,
    class: null,
  },
})

const EnhancedCodeBlockLowlight = CodeBlockLowlight.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      language: {
        default: null,
        parseHTML: (element) => {
          const preClass = element.className || ""
          const preMatch = preClass.match(/language-(\w+)/)

          if (preMatch) {
            return resolveLanguageAlias(preMatch[1])
          }

          const codeElement = element.querySelector("code")
          if (codeElement) {
            const codeClass = codeElement.className || ""
            const codeMatch = codeClass.match(/language-(\w+)/)

            if (codeMatch) {
              return resolveLanguageAlias(codeMatch[1])
            }
          }

          return null
        },
        renderHTML: (attributes) => {
          if (!attributes.language) {
            return {}
          }

          return {
            class: `language-${attributes.language}`,
          }
        },
      },
    }
  },
}).configure({
  lowlight,
  enableTabIndentation: true,
  tabSize: 2,
  languageClassPrefix: "language-",
  defaultLanguage: null,
  HTMLAttributes: {
    style: null,
    class: null,
  },
})

export {
  Audio,
  Button,
  Cite,
  CustomDetails,
  CustomDetailsContent,
  CustomDetailsSummary,
  Dd,
  Dfn,
  Div,
  EnhancedBulletList,
  EnhancedCodeBlockLowlight,
  EnhancedListItem,
  EnhancedOrderedList,
  EnhancedParagraph,
  EnhancedTableHeader,
  EnhancedTableKit,
  EnhancedTableRow,
  EnhancedTextStyleKit,
  EnhancedYoutube,
  Figure,
  Footer,
  Iframe,
  Input,
  Ins,
  Kbd,
  Label,
  Option,
  Select,
  Span,
  TableCellWithBorders,
  TableWithStyles,
  Video,
  lowlight,
}
